export const APP_NAME = 'LYTMASTER';
export const APP_TAGLINE = 'Venture Builder Cost Dashboard';

export const VENTURES = ['markondo', 'sevennup', 'teder', 'lokkout'] as const;
export type VentureName = (typeof VENTURES)[number];

export const VENTURE_LABELS: Record<VentureName, string> = {
  markondo: 'Markondo',
  sevennup: 'SevenNup',
  teder: 'Teder',
  lokkout: 'Lokkout',
};

export interface VentureRepoConfig {
  owner: string;
  repo: string;
  defaultBranch: string;
  infraPaths: string[];
  appPaths?: string[];
}

export const VENTURE_REPOS: Record<VentureName, VentureRepoConfig> = {
  markondo: {
    owner: 'lyt-ventures',
    repo: 'markondo-v2',
    defaultBranch: 'main',
    infraPaths: [
      'infrastructure/lib/api-stack.ts',
      'infrastructure/lib/database-stack.ts',
      'infrastructure/lib/storage-stack.ts',
      'infrastructure/lib/cdn-stack.ts',
      'infrastructure/lib/config.ts',
    ],
  },
  sevennup: {
    owner: 'lyt-ventures',
    repo: 'sevennup-v2',
    defaultBranch: 'main',
    infraPaths: [
      'infra/lib/stacks/backend-stack.ts',
      'infra/lib/stacks/database-stack.ts',
      'infra/lib/stacks/network-stack.ts',
      'infra/lib/stacks/frontend-stack.ts',
    ],
  },
  teder: {
    owner: 'lyt-ventures',
    repo: 'teder-v2',
    defaultBranch: 'main',
    infraPaths: [
      'teder-api/serverless.yml',
    ],
  },
  lokkout: {
    owner: 'lyt-ventures',
    repo: 'lokkout-v2',
    defaultBranch: 'main',
    infraPaths: [
      'infrastructure/lib/api-stack.ts',
      'infrastructure/lib/database-stack.ts',
      'infrastructure/lib/storage-stack.ts',
      'infrastructure/lib/cdn-stack.ts',
      'infrastructure/lib/config.ts',
    ],
  },
};

export const AWS_SERVICES = [
  'Amazon EC2',
  'Amazon RDS',
  'AWS Lambda',
  'Amazon S3',
  'Amazon CloudFront',
  'Amazon API Gateway',
  'Amazon Cognito',
  'Amazon SES',
  'AWS Secrets Manager',
  'Amazon Route 53',
  'AWS Key Management Service',
  'Amazon CloudWatch',
] as const;
export type AwsService = (typeof AWS_SERVICES)[number];

export const TIME_GRANULARITIES = ['DAILY', 'MONTHLY'] as const;
export type TimeGranularity = (typeof TIME_GRANULARITIES)[number];

export interface CostDataPoint {
  date: string;
  amount: number;
  service: string;
  venture: string;
}

export interface VentureCostSummary {
  venture: VentureName;
  currentMonthTotal: number;
  previousMonthTotal: number;
  percentChange: number;
  topServices: { service: string; amount: number }[];
}

export interface CostForecast {
  venture: VentureName;
  forecastedTotal: number;
  forecastStart: string;
  forecastEnd: string;
}

export interface OptimizationReview {
  id: number;
  reviewDate: string;
  reportMarkdown: string;
  costDataJson: Record<string, unknown>;
  modelUsed: string;
  createdAt: string;
}

export type StackStatus = 'running' | 'hibernated' | 'transitioning';
export type StackResourceType =
  | 'rds'
  | 'aurora'
  | 'ec2'
  | 'ecs'
  | 'lambda'
  | 'cloudfront'
  | 's3'
  | 'cognito'
  | 'other';

export interface StackResource {
  type: StackResourceType;
  id: string;
  name: string;
  status: 'running' | 'stopped' | 'scaling';
  canHibernate: boolean;
}

export interface VentureStack {
  stackName: string;
  venture: VentureName;
  status: StackStatus;
  costPerDay: number;
  costPerMonth: number;
  resources: StackResource[];
}

export function formatCurrency(amount: number, currency = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

export function formatDate(date: string | Date): string {
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(new Date(date));
}

export function formatDateTime(date: string | Date): string {
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(date));
}

export function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

export function percentChange(current: number, previous: number): number {
  if (previous === 0) return current > 0 ? 100 : 0;
  return ((current - previous) / previous) * 100;
}
